import * as React from "react";
import { ShieldAlert, Code, Loader2, Mail, Clock, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClientSelector } from "./ClientSelector";
import { CountrySelector } from "./CountrySelector";

export const CreateAlertCard = ({ onCreateAlert, isLoading }) => {
  const [client, setClient] = React.useState("");
  const [selectedCountries, setSelectedCountries] = React.useState([]);
  const [alertName, setAlertName] = React.useState("");
  const [showQuery, setShowQuery] = React.useState(false);
  const [notificationEmails, setNotificationEmails] = React.useState([]);
  const [emailInput, setEmailInput] = React.useState("");
  const [scheduleInterval, setScheduleInterval] = React.useState("1h");
  const [emailError, setEmailError] = React.useState("");
  const [showDomainSuggestions, setShowDomainSuggestions] = React.useState(false);

  // Email domains for autocomplete
  const EMAIL_DOMAINS = [
    "@latamdigitalmarketing.com",
    "@endavantgroup.com",
    "@perform-ly.com"
  ];

  const isFormValid = client && selectedCountries.length > 0 && alertName.trim() && notificationEmails.length > 0;

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    
    if (!validateEmail(email)) {
      setEmailError("Formato de email inválido");
      return;
    }
    
    if (notificationEmails.includes(email)) {
      setEmailError("Este email ya está agregado");
      return;
    }
    
    setNotificationEmails([...notificationEmails, email]);
    setEmailInput("");
    setEmailError("");
    setShowDomainSuggestions(false);
  };

  const handleRemoveEmail = (emailToRemove) => {
    setNotificationEmails(notificationEmails.filter(e => e !== emailToRemove));
  };

  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);
    setEmailError("");
    
    // Show domain suggestions when @ is typed and no domain yet
    // Check if there's an @ and no dot AFTER the @
    const atIndex = value.indexOf("@");
    if (atIndex > 0 && !value.substring(atIndex).includes(".")) {
      setShowDomainSuggestions(true);
    } else {
      setShowDomainSuggestions(false);
    }
  };

  const handleSelectDomain = (domain) => {
    const username = emailInput.split("@")[0];
    setEmailInput(username + domain);
    setShowDomainSuggestions(false);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const generateQueryPreview = () => {
    if (!client || selectedCountries.length === 0) return "";
    
    const countryConditions = selectedCountries
      .map((code) => `country_code <> '${code}'`)
      .join(" AND ");

    return `SELECT
  COUNT(*) AS paises_no_autorizados
FROM (
    SELECT DISTINCT gt.country_code
    FROM datahub_prod.google_ads.ad_stats g
    JOIN datahub_prod.google_ads.campaign_criterion_history c
      ON regexp_extract(g.campaign_base_campaign, 'campaigns/([0-9]+)', 1) = CAST(c.campaign_id AS STRING)
      AND c.geo_target_constant_id IS NOT NULL
    JOIN datahub_prod.google_ads.geo_target gt
      ON c.geo_target_constant_id = gt.id
    WHERE g.customer_id IN (
        SELECT id FROM datahub_prod.mapper.accounts
        WHERE cliente = '${client}'
    )
    AND g.date >= DATE_SUB(CURRENT_DATE(), 90)
)
WHERE ${countryConditions}`;
  };

  const handleSubmit = () => {
    if (!isFormValid) return;
    
    onCreateAlert({
      client_name: client,
      authorized_countries: selectedCountries,
      alert_name: alertName.trim(),
      notification_emails: notificationEmails,
      schedule_interval: scheduleInterval,
    });
  };

  const handleReset = () => {
    setClient("");
    setSelectedCountries([]);
    setAlertName("");
    setShowQuery(false);
    setNotificationEmails([]);
    setEmailInput("");
    setScheduleInterval("1h");
    setEmailError("");
  };

  return (
    <Card className="bg-slate-50/50 border-dashed border-2 border-slate-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-900 rounded-lg">
            <ShieldAlert className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold tracking-tight">
              Crear Nueva Alerta
            </CardTitle>
            <CardDescription className="text-slate-500">
              Configura una alerta para monitorear campañas en países no autorizados
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Alert Name */}
          <div className="md:col-span-12 space-y-2">
            <Label htmlFor="alertName" className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Nombre de la Alerta <span className="text-red-500">*</span>
            </Label>
            <Input
              id="alertName"
              placeholder="Ej: Alerta Cochez - Solo Panamá"
              value={alertName}
              onChange={(e) => setAlertName(e.target.value)}
              className="bg-white border-slate-200 h-11"
              data-testid="alert-name-input"
            />
          </div>

          {/* Client Selector */}
          <div className="md:col-span-4 space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Cliente <span className="text-red-500">*</span>
            </Label>
            <ClientSelector value={client} onValueChange={setClient} />
          </div>

          {/* Country Selector */}
          <div className="md:col-span-8 space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Países Autorizados <span className="text-red-500">*</span>
            </Label>
            <CountrySelector
              selectedCountries={selectedCountries}
              onSelectionChange={setSelectedCountries}
            />
          </div>

          {/* Schedule Interval */}
          <div className="md:col-span-4 space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Clock className="h-3 w-3" />
              Frecuencia de Ejecución
            </Label>
            <Select value={scheduleInterval} onValueChange={setScheduleInterval}>
              <SelectTrigger className="bg-white border-slate-200 h-11">
                <SelectValue placeholder="Seleccionar frecuencia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Cada hora</SelectItem>
                <SelectItem value="6h">Cada 6 horas</SelectItem>
                <SelectItem value="daily">Diario</SelectItem>
                <SelectItem value="weekly">Semanal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notification Emails */}
          <div className="md:col-span-8 space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Mail className="h-3 w-3" />
              Emails para Notificaciones <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="nombre@dominio.com"
                  value={emailInput}
                  onChange={handleEmailInputChange}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={() => setTimeout(() => setShowDomainSuggestions(false), 150)}
                  className="bg-white border-slate-200 h-11 flex-1"
                  data-testid="email-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddEmail}
                  className="h-11 px-3 border-slate-200 hover:bg-slate-100"
                  data-testid="add-email-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {/* Domain autocomplete dropdown */}
              {showDomainSuggestions && (
                <div className="absolute z-50 top-12 left-0 w-full max-w-md bg-white border border-slate-200 rounded-md shadow-lg">
                  <p className="px-3 py-2 text-xs text-slate-500 border-b">Selecciona un dominio:</p>
                  {EMAIL_DOMAINS.map((domain) => (
                    <button
                      key={domain}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
                      onClick={() => handleSelectDomain(domain)}
                    >
                      <span className="text-slate-400">{emailInput.split("@")[0]}</span>
                      <span className="font-medium text-slate-700">{domain}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {emailError && (
              <p className="text-xs text-red-500">{emailError}</p>
            )}
            {notificationEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {notificationEmails.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 pr-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-1 p-0.5 rounded-full hover:bg-slate-300"
                      data-testid={`remove-email-${email}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Query Preview */}
        {client && selectedCountries.length > 0 && (
          <Collapsible open={showQuery} onOpenChange={setShowQuery}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                data-testid="toggle-query-preview"
              >
                <span className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Vista previa del Query SQL
                </span>
                <span className="text-xs">{showQuery ? "Ocultar" : "Mostrar"}</span>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-2 p-4 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto font-mono">
                {generateQueryPreview()}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2">
          {!isFormValid && (
            <p className="text-xs text-amber-600">
              {!client && "• Selecciona un cliente. "}
              {selectedCountries.length === 0 && "• Selecciona al menos un país. "}
              {!alertName.trim() && "• Ingresa un nombre para la alerta. "}
              {notificationEmails.length === 0 && "• Agrega al menos un correo de notificación."}
            </p>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white h-11"
            data-testid="create-alert-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creando...
              </>
            ) : (
              "Crear Alerta en Databricks"
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-slate-200 hover:bg-slate-100"
            data-testid="reset-form-btn"
          >
            Limpiar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
